"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@drpcrd/utils/shared";
import {
  ComponentPropsWithoutRef,
  ComponentRef,
  HTMLAttributes,
  forwardRef,
} from "react";
export { type DialogProps } from "@radix-ui/react-dialog";

export const Dialog = DialogPrimitive.Root;

export const DialogTrigger = forwardRef<
  ComponentRef<typeof DialogPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Trigger>
>(({ ...props }, ref) => (
  <DialogPrimitive.Trigger
    onClick={(e) => e.stopPropagation()}
    ref={ref}
    {...props}
  />
));
DialogTrigger.displayName = DialogPrimitive.Trigger.displayName;

export const DialogPortal = DialogPrimitive.Portal;

export const DialogClose = DialogPrimitive.Close;

export const DialogOverlay = forwardRef<
  ComponentRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    onClick={(e) => e.stopPropagation()}
    ref={ref}
    className={cn(
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/60 backdrop-blur-xs",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

export const DialogContent = forwardRef<
  ComponentRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideCloseButton?: boolean;
  }
>(({ className, children, hideCloseButton, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      onClick={(e) => e.stopPropagation()}
      ref={ref}
      className={cn(
        "data-[state=closed]:animate-out! lg:data-[state=open]:animate-in lg:data-[state=closed]:fade-out-0! lg:data-[state=open]:fade-in-0 lg:data-[state=closed]:zoom-out-95! lg:data-[state=open]:zoom-in-95 fixed top-[0%] left-[0%] z-50 flex h-full w-full flex-col overflow-hidden bg-neutral-100 shadow-lg duration-200 md:w-full lg:top-[50%] lg:left-[50%] lg:h-auto lg:max-h-[calc(100vh-64px)] lg:max-w-[800px] lg:-translate-x-[50%] lg:-translate-y-[50%] lg:rounded-2xl",
        className,
      )}
      {...props}
    >
      {children}
      {!hideCloseButton && (
        <DialogPrimitive.Close className="absolute top-4 right-3 cursor-pointer rounded-xs ring-offset-white transition-opacity disabled:pointer-events-none sm:top-4 sm:right-4">
          <X className="h-3 w-3 text-neutral-900 hover:text-black" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

export const DialogHeader = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("shadow-top-bar flex flex-col bg-white text-left", className)}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

export const DialogFooter = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse bg-white px-4 py-3 sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

export const DialogTitle = forwardRef<
  ComponentRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "px-2 py-3 text-lg leading-[24px] font-semibold sm:px-4 sm:py-4 sm:text-xl sm:leading-[24px]",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

export const DialogDescription = forwardRef<
  ComponentRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-slate-500", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
